import {
  ADMIN_UPDATEABLE_PLAN_FAMILIES,
  SUBSCRIBABLE_PLAN_FAMILIES,
  UPDATEABLE_PLAN_FAMILIES,
} from '@localstack/constants';
import { CreditCard, Plan, PlanFamily, Subscription } from '@localstack/types';

interface ValidationErrorProps {
  hasOutstandingKeys: boolean;
  adminMode?: boolean;
  isCardRequired: boolean;
  card: Optional<CreditCard>;
  plan: Plan;
  subscription: Optional<Subscription>;
}

/** Obtains a string with the first validation error, if any, for the given props */
export const getFirstValidationError: (props: ValidationErrorProps) => Optional<string> = ({
  hasOutstandingKeys,
  adminMode,
  isCardRequired,
  card,
  plan,
  subscription,
}) => {
  const hasCorrectCurrency = !subscription || subscription.currency === plan.currency;

  if (!hasCorrectCurrency) {
    return 'This subscription cannot be updated due to a currency mismatch';
  }
  if (hasOutstandingKeys) {
    return 'Please cancel outstanding keys to proceed';
  }
  if (isCardRequired && !card) {
    return 'No payment method selected.';
  }

  // at this point all must-have requirements are met, skip other checks for admins
  if (adminMode) {
    return undefined;
  }
  return undefined;
};

/**
 * Returns a filtered and sorted list of plans to be displayed
 *
 * When `isFreemiumHobbyCommercialMisuse` is true, the entire FREEMIUM_PLANS family is hidden —
 * mirrors the backend gate for workspaces flagged for commercial misuse of the Freemium Hobby plan.
 */
export const getSortedPlans = (
  plans: Plan[],
  subscription: Optional<Subscription>,
  adminMode?: boolean,
  defaultPlan?: Optional<Plan>,
  isFreemiumHobbyCommercialMisuse?: boolean,
): Plan[] => {
  const filteredPlans = plans.filter((plan) => {
    const editMode = !!subscription;

    // hide the entire freemium family for users flagged for commercial misuse —
    // belt-and-suspenders backstop in case a restricted plan slips through the API filter
    if (isFreemiumHobbyCommercialMisuse && !adminMode && plan.family === PlanFamily.FREEMIUM_PLANS) {
      return false;
    }

    // hide all freemium plans from the picker for an owner already on a freemium plan.
    // an owner managing a freemium subscription should never be offered another freemium
    // option (legacy plan.freemium and the new plan.freemium-2026-03-23 would otherwise
    // both surface - own-plan via line 89 below, successor via line 95). this also
    // prevents an owner of the legacy freemium from being silently migrated to the
    // successor on a seat change. non-freemium upgrade paths still work as before.
    if (
      editMode &&
      !adminMode &&
      subscription?.plan.family === PlanFamily.FREEMIUM_PLANS &&
      plan.family === PlanFamily.FREEMIUM_PLANS
    ) {
      return false;
    }

    // make the owned plan visible on the update screen to enable update within decommissioned/legacy plans
    if (!adminMode && !plan.public && plan.id !== subscription?.plan.id) {
      return false;
    }

    if (adminMode) {
      return !editMode || ADMIN_UPDATEABLE_PLAN_FAMILIES.includes(plan.family);
    }

    // always include currently subscribed plan
    // this only works if we still offer the currently provided plan
    // see next check for a fallback to superseding plan
    if (editMode && plan.id === subscription?.plan.id) {
      return true;
    }

    // always include plans superseding the currently subscribed plan
    // this is needed to have controlled upgrade paths from legacy plans
    if (editMode && subscription && plan.supersedes_plan_ids?.includes(subscription.plan.id)) {
      return true;
    }

    // Hide freemium plan
    if (!adminMode && plan.family === PlanFamily.FREEMIUM_PLANS) {
      return false;
    }

    // trial is subscribed to directly from the pricing page, never through checkout
    if (plan.family === PlanFamily.TRIAL_PLANS) {
      return false;
    }

    // remove hobby unless preselected
    if (plan.family === PlanFamily.HOBBY_PLANS && !(defaultPlan?.family === PlanFamily.HOBBY_PLANS)) {
      return false;
    }

    // remove regular monthly plans
    if (
      (plan.family === PlanFamily.PRO_PLANS && plan.interval_months === 1) ||
      (plan.family === PlanFamily.TEAM_PLANS && plan.interval_months === 1)
    ) {
      return false;
    }

    // remove hobby plans from plans list on updates
    if (editMode && plan.family === PlanFamily.HOBBY_PLANS) {
      return false;
    }

    // remove student
    if (!adminMode && plan.family === PlanFamily.STUDENT) {
      return false;
    }

    return editMode ? UPDATEABLE_PLAN_FAMILIES.includes(plan.family) : SUBSCRIBABLE_PLAN_FAMILIES.includes(plan.family);
  });

  const sortedPlans = filteredPlans
    .slice()
    .sort(
      (lp, rp) =>
        SUBSCRIBABLE_PLAN_FAMILIES.indexOf(lp.family as PlanFamily) -
        SUBSCRIBABLE_PLAN_FAMILIES.indexOf(rp.family as PlanFamily),
    );

  return sortedPlans;
};
